export const studyGuidesData = [
  {
    unitNumber: 1,
    unitTitle: "Number Systems & Expressions",
    keyFormulas: [
      "a^m × a^n = a^(m+n)",
      "a^m ÷ a^n = a^(m-n)",
      "(a^m)^n = a^(mn)",
      "a^0 = 1 (where a ≠ 0)",
      "a^(-n) = 1/a^n",
      "√(ab) = √a × √b",
      "Distance = Rate × Time"
    ],
    vocabulary: [
      { term: "Rational Number", definition: "A number that can be expressed as a fraction p/q where q ≠ 0" },
      { term: "Irrational Number", definition: "A number that cannot be expressed as a simple fraction (e.g., π, √2)" },
      { term: "Exponent", definition: "The power to which a number is raised" },
      { term: "Scientific Notation", definition: "A way to express very large or small numbers: a × 10^n where 1 ≤ a < 10" },
      { term: "Radical", definition: "An expression containing a root symbol (√)" }
    ],
    importantRules: [
      "The product of two rational numbers is always rational",
      "The sum of a rational and an irrational number is always irrational",
      "When multiplying powers with the same base, add the exponents",
      "When dividing powers with the same base, subtract the exponents",
      "A negative exponent means take the reciprocal",
      "√(a²) = |a| (absolute value ensures positive result)"
    ],
    exampleProblems: [
      { 
        problem: "Simplify: (3x²y³)²", 
        solution: "= 3² × (x²)² × (y³)² = 9x⁴y⁶" 
      },
      { 
        problem: "Express 0.00045 in scientific notation", 
        solution: "= 4.5 × 10^(-4)" 
      },
      { 
        problem: "Simplify: √50", 
        solution: "= √(25 × 2) = 5√2" 
      }
    ]
  },
  {
    unitNumber: 2,
    unitTitle: "Equations & Inequalities",
    keyFormulas: [
      "Quadratic Formula: x = (-b ± √(b²-4ac)) / 2a",
      "Difference of Squares: a² - b² = (a+b)(a-b)",
      "Perfect Square Trinomial: a² + 2ab + b² = (a+b)²",
      "Slope Formula: m = (y₂-y₁)/(x₂-x₁)",
      "Point-Slope Form: y - y₁ = m(x - x₁)",
      "Standard Form: Ax + By = C"
    ],
    vocabulary: [
      { term: "Linear Equation", definition: "An equation whose graph is a straight line (degree 1)" },
      { term: "Quadratic Equation", definition: "An equation of degree 2 in the form ax² + bx + c = 0" },
      { term: "System of Equations", definition: "Two or more equations with the same variables" },
      { term: "Inequality", definition: "A mathematical statement using <, >, ≤, or ≥" },
      { term: "Factoring", definition: "Breaking down an expression into products of simpler expressions" }
    ],
    importantRules: [
      "When multiplying or dividing an inequality by a negative number, flip the inequality sign",
      "To solve a system, use substitution, elimination, or graphing",
      "The discriminant (b²-4ac) determines the number of real solutions",
      "If b²-4ac > 0: two real solutions; = 0: one solution; < 0: no real solutions",
      "Always check solutions in the original equation",
      "Compound inequalities use 'and' (intersection) or 'or' (union)"
    ],
    exampleProblems: [
      { 
        problem: "Solve: 2x + 5 = 13", 
        solution: "2x = 8 → x = 4" 
      },
      { 
        problem: "Factor: x² - 9", 
        solution: "(x + 3)(x - 3)" 
      },
      { 
        problem: "Solve using quadratic formula: x² + 5x + 6 = 0", 
        solution: "x = (-5 ± √(25-24))/2 = (-5 ± 1)/2 → x = -2 or x = -3" 
      }
    ]
  },
  {
    unitNumber: 3,
    unitTitle: "Functions & Graphing",
    keyFormulas: [
      "Slope-Intercept Form: y = mx + b",
      "Standard Form: Ax + By = C",
      "Point-Slope Form: y - y₁ = m(x - x₁)",
      "Slope: m = (y₂-y₁)/(x₂-x₁)",
      "Distance Formula: d = √((x₂-x₁)² + (y₂-y₁)²)",
      "Midpoint Formula: M = ((x₁+x₂)/2, (y₁+y₂)/2)",
      "Vertex Form: y = a(x-h)² + k"
    ],
    vocabulary: [
      { term: "Function", definition: "A relation where each input has exactly one output" },
      { term: "Domain", definition: "The set of all possible input values (x-values)" },
      { term: "Range", definition: "The set of all possible output values (y-values)" },
      { term: "Slope", definition: "The rate of change; rise over run (m = Δy/Δx)" },
      { term: "Y-intercept", definition: "The point where a line crosses the y-axis (x = 0)" }
    ],
    importantRules: [
      "Vertical Line Test: If a vertical line intersects a graph more than once, it's not a function",
      "Parallel lines have equal slopes (m₁ = m₂)",
      "Perpendicular lines have negative reciprocal slopes (m₁ × m₂ = -1)",
      "Function notation: f(x) means 'the value of f at x'",
      "Transformations: f(x) + k shifts up k units; f(x+h) shifts left h units",
      "The vertex of y = a(x-h)² + k is at (h, k)"
    ],
    exampleProblems: [
      { 
        problem: "Find slope between (2,3) and (5,9)", 
        solution: "m = (9-3)/(5-2) = 6/3 = 2" 
      },
      { 
        problem: "Write equation with slope 3 through (1,4)", 
        solution: "y - 4 = 3(x - 1) → y = 3x + 1" 
      },
      { 
        problem: "If f(x) = 2x + 5, find f(3)", 
        solution: "f(3) = 2(3) + 5 = 11" 
      }
    ]
  },
  {
    unitNumber: 4,
    unitTitle: "Statistics & Data Analysis",
    keyFormulas: [
      "Mean: x̄ = Σx / n",
      "Range: Maximum - Minimum",
      "Standard Deviation: σ = √(Σ(x-x̄)²/n)",
      "Probability: P(event) = (favorable outcomes) / (total outcomes)",
      "Linear Regression: y = mx + b (best fit line)",
      "Interquartile Range: IQR = Q₃ - Q₁"
    ],
    vocabulary: [
      { term: "Mean", definition: "The average of a data set (sum divided by count)" },
      { term: "Median", definition: "The middle value when data is ordered" },
      { term: "Mode", definition: "The most frequently occurring value" },
      { term: "Outlier", definition: "A data point significantly different from others" },
      { term: "Correlation", definition: "A relationship between two variables (positive, negative, or none)" }
    ],
    importantRules: [
      "Mean is affected by outliers; median is resistant to outliers",
      "For symmetric distributions, mean ≈ median",
      "Standard deviation measures spread; larger σ means more variability",
      "Correlation does not imply causation",
      "Positive correlation: as x increases, y increases",
      "Negative correlation: as x increases, y decreases",
      "Probability always ranges from 0 to 1 (or 0% to 100%)",
      "P(A or B) = P(A) + P(B) - P(A and B)"
    ],
    exampleProblems: [
      { 
        problem: "Find mean of: 4, 7, 9, 12, 13", 
        solution: "x̄ = (4+7+9+12+13)/5 = 45/5 = 9" 
      },
      { 
        problem: "Find median of: 3, 8, 12, 15, 20", 
        solution: "Median = 12 (middle value)" 
      },
      { 
        problem: "Probability of rolling an even number on a die", 
        solution: "P(even) = 3/6 = 1/2 = 0.5 or 50%" 
      }
    ]
  }
];